import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import shallow from "zustand/shallow";
import PropTypes from "prop-types";

import eventService from "@/js/services/eventService";
import useStore from "@/stores/index";
import { getChatWindowOpen, getMessagesForThreadKey } from "@/stores/chatStore";
import { isMembershipOwnershipDisabled } from "@/stores/classwizeConfigStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { sendChatNotificaton } from "@/js/utils/notifications";
import useClickOutside from "@/js/utils/outsideClickUtils";
import { userDisplayText } from "@/js/utils/userUtils";
import ChatToolbarButton from "./components/ChatToolbarButton";
import ChatWindow from "./components/ChatWindow";
import DisplayNameModal from "./components/DisplayNameModal";
import { isChatFeatureEnabled } from "@/js/utils/chatUtils";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const ClassroomChat = ({ classroom, ...props }) => {
	const location = useLocation();
	const [isOpen, setIsOpen] = useState(getChatWindowOpen(useStore.getState()));
	const [isEnabled, setIsEnabled] = useState(false);
	const [, setAllowPushNotifications] = useState(false);
	const [windowOffset, setWindowOffset] = useState(0);
	const chatWrapperRef = useRef();

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const scrollHandler = (_) => {
		if (props.navRef && props.navRef.current) {
			const rect = props.navRef.current.getBoundingClientRect();

			if (rect.top && rect.top <= 0) {
				setWindowOffset(rect.top);
			} else {
				setWindowOffset(0);
			}
		}
	};

	useEffect(() => {
		if (isEnabled) {
			window.addEventListener("scroll", scrollHandler, { capture: true });

			useStore.getState().chat.fetchOverview();
			addEventServiceListeners();
			const unsubEventServiceStatus = useStore.subscribe(
				(state) => state.eventServiceStatus.status,

				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				(status) => {
					addEventServiceListeners();
				}
			);
			useStore.getState().chat.fetchDisplayName({ username: localStorage.getItem("USERNAME") });
			const unsubChatOpen = useStore.subscribe(
				(state) => state.chat.chatWindowOpen,
				(newisOpen) => setIsOpen(newisOpen),
				shallow
			);

			if ("Notification" in window) {
				if (Notification.permission === "granted") {
					setAllowPushNotifications(true);
				} else if (Notification.permission !== "denied" || Notification.permission === "default") {
					Notification.requestPermission((permission) => {
						setAllowPushNotifications(permission === "granted");
					});
				}
			}

			return () => {
				window.removeEventListener("scroll", scrollHandler, { capture: true });
				unsubChatOpen();
				unsubEventServiceStatus();
				removeEventServiceListeners();
			};
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEnabled]);

	useEffect(() => {
		if (useStore.getState().chat.displayName.error && !useStore.getState().chat.displayName.isFetching) {
			useStore.getState().chat.fetchDisplayName({ username: localStorage.getItem("USERNAME") });
		}
		if (useStore.getState().chat.unreadMessagesCount.error && !useStore.getState().chat.unreadMessagesCount.isFetching) {
			useStore.getState().chat.fetchOverview();
		}
	}, [isOpen]);

	useEffect(() => {
		if (isChatFeatureEnabled()) {
			// chat is not available if membership and ownership are both disabled
			if (isMembershipOwnershipDisabled(useStore.getState())) {
				return;
			}

			setIsEnabled(true);
		}
	}, []);

	const isNotificationForCurrentClassroomInView = (messageClassroomId) => {
		return location.pathname === "/classroom/details" && messageClassroomId === localStorage.getItem("ACTIVE_GROUP");
	};

	const addEventServiceListeners = () => {
		if (eventService.eventSource) {
			eventService.eventSource.addEventListener("MESSAGE", handleMessage);
		}
	};

	const removeEventServiceListeners = () => {
		if (eventService.eventSource) {
			eventService.eventSource.removeEventListener("MESSAGE", handleMessage);
		}
	};

	const handleMessage = useCallback((event) => {
		const chat = useStore.getState().chat;

		const currentUser = localStorage.getItem("USERNAME");
		let eventData;
		try {
			eventData = JSON.parse(event.data);
		} catch (err) {
			console.log(err);
		}
		if (!eventData.participantIds.includes(currentUser)) {
			console.log("Recieved message for other user");
			return;
		}

		useStore.getState().chat.fetchMessages(
			{
				classroomId: eventData.classroomId,
				threadKey: eventData.threadKey,
				maxResults: 5,
			},
			true,
			false,
			true
		);

		getMessagesForThreadKey(useStore.getState(), eventData.threadKey);

		if (eventData.sender.id !== currentUser) {
			const currentClass = useUserSessionStore.getState().getActiveGroup();
			let showClassroomName = false;

			if (currentClass !== eventData.classroomId) {
				showClassroomName = true;
			}

			const users = useStore.getState().users.data;
			const senderUser = users[eventData.sender.id];
			const senderDisplayText = senderUser ? userDisplayText(senderUser) : "";

			// we also only want to do push notifications for messages sent from students in this class
			if (isNotificationForCurrentClassroomInView(eventData.classroomId) && Object.keys(users).includes(eventData.sender.id)) {
				sendChatNotificaton(
					eventData.threadKey,
					eventData.classroomId,
					eventData.sender.id,
					eventData.msg,
					showClassroomName,
					senderDisplayText
				);
			}

			const chatOverview = chat.unreadMessagesCount.data;

			if (!chat.selectedStudentName && !chat.selectedThreadKey) {
				if (chatOverview[eventData.threadKey] && !!chatOverview[eventData.threadKey].hasUnreadMessages) {
					chat.updateChatOverviewData(chatOverview);
				} else {
					chat.fetchOverview();
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const toggleIsOpen = (evt) => {
		evt.preventDefault();
		useStore.getState().chat.toggleChatWindow();
	};

	const handleClickOutside = () => {
		if (isOpen && !useStore.getState().chat.chatToggleModalOpen) {
			useStore.getState().chat.toggleChatWindow();
		}
	};

	useClickOutside([chatWrapperRef], handleClickOutside);

	if (!isEnabled) {
		return null;
	}

	return (
		<div className="ClassroomChat_wrapper" ref={chatWrapperRef}>
			<ChatToolbarButton handleClick={toggleIsOpen} classroom={classroom} {...props} />
			{isOpen && (
				<ChatWindow classroom={classroom} isChatWorking={!!useStore.getState().chat.unreadMessagesCount.data} windowOffset={windowOffset} />
			)}
			<DisplayNameModal />
		</div>
	);
};

ClassroomChat.propTypes = {
	classroom: PropTypes.object,
	navRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
};

export default ClassroomChat;
